import * as joda from "@js-joda/core";
import currency from "currency.js";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { APISpec } from "trader.ts/router";
import { Source } from "trader.ts/types/source";
import { dyn } from "trader.ts/ui";
import {
  Field,
  Form,
  Parsing,
  checkBox,
  constantField,
  dateBox,
  selectBox,
  textBox,
} from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  formulachargemode,
  lessongroupid,
  payment_scheme_non_periodic_id,
  periodic_payment_method,
  periodid,
  price_adapted_to_lg_and_student,
  studentid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { makeArrayField, oneLineArrayRender } from "../../client/arrayfield";
import { constantFieldWithRender } from "../../client/constantfieldwithrender";
import { radioFieldCheckboxRenderInputLike } from "../../client/radiofield";
import { typeaheadField } from "../../client/typeaheadfield";
import {
  country,
  non_periodic_payment_scheme,
  periodic_payment_scheme,
} from "../../domain/config";
import {
  adaptInvoiceLinesToStudent,
  calculateExpectedPayments,
  getExtraCostInvoiceLine,
  periodfeeAfterChoice,
} from "../../domain/finance";
import {
  invoicelineLG,
  invoicelineRequestedOptional,
  invoicelineStudent,
} from "../../domain/finance/types";
import { emailValidation } from "../../lib/emailisvalid";
import { isEqual } from "../../lib/isequal";
import { mandatoryValidation } from "../../lib/validate";
import {
  invoicelinetype,
  singleDueDateCalculation,
} from "../../parsing_from_ur";
import {
  ddMMMyyyy,
  renderCurrency,
  renderFirstNameLastName,
  renderPaymentScheme,
} from "../../render";
import * as routes from "../../routes";
import { schoolurl } from "../../types";

export function mkEnrollmentFormOnPlanning(
  lg:
    | { tag: "private"; ils: invoicelineLG[] }
    | {
        tag: "group";
        lessongroupid: lessongroupid;
        allow_trial_lessons: boolean;
        trial_lesson_payment_scheme_id: null | payment_scheme_non_periodic_id;
      },
  payment_schemes: (non_periodic_payment_scheme | periodic_payment_scheme)[],
  country: country,
  schoolurl: schoolurl,
  periodid: periodid,
  dates: {
    firstlesson: joda.LocalDate;
    lastlesson: joda.LocalDate;
    period_firstday: joda.LocalDate;
    created: joda.LocalDate;
    numberOfLessons: number;
    lessons: joda.LocalDate[];
    startedlessonson: joda.LocalDate;
  },
  initialstudent:
    | null
    | {
        tag: "new";
        firstname: string;
        lastname: string;
        email: string;
        phone: string;
        birthday: joda.LocalDate | null;
      }
    | {
        tag: "existing";
        student: {
          id: studentid;
          firstname: string;
          lastname: string;
          birthday: joda.LocalDate | null;
        };
      },
  each_period_fee_due_date: singleDueDateCalculation,
  each_period_fee_pay_during_online: boolean,
  opts: {
    max_one_optional_line: boolean;
    periodfee_required: boolean;
  },
): Field<
  { new_or_existing: "new" | "existing" } & {
    student:
      | {
          tag: "new";
          firstname: string;
          lastname: string;
          email: string;
          phone: string;
          birthday: joda.LocalDate | null;
        }
      | {
          tag: "existing";
          student: {
            id: studentid;
            firstname: string;
            lastname: string;
            birthday: joda.LocalDate | null;
          };
        };
    trial_lesson: boolean;
    invoice_and_expected_payments: {
      invoice: invoicelineStudent[];
      payment_scheme: non_periodic_payment_scheme | periodic_payment_scheme;
      expected_payments: {
        amount: currency;
        date: joda.LocalDate;
        pay_during_online_enrollment: boolean;
        periodic_payment_method: periodic_payment_method | null;
      }[];
      periodfee: periodfeeAfterChoice;
    };
  }
> {
  return new Form({})
    .addField("new_or_existing", [], async function (): Promise<
      Field<"new" | "existing">
    > {
      const s: Source<Parsing<"new" | "existing">> = new Source({
        tag: "parsed",
        parsed: initialstudent === null ? "existing" : initialstudent.tag,
      });
      return {
        s,
        cleanup: () => {},
        render: () => {
          return (
            <article style="margin-top: -12px; text-align: right;">
              {dyn(s, function (f) {
                if (f.tag === "parsed") {
                  if (f.parsed === "existing") {
                    return (
                      <a
                        onclick={() => s.set({ tag: "parsed", parsed: "new" })}
                      >
                        ...of schrijf <b>nieuwe</b> student in
                      </a>
                    );
                  } else if (f.parsed === "new") {
                    return (
                      <a
                        onclick={() =>
                          s.set({ tag: "parsed", parsed: "existing" })
                        }
                      >
                        ...of schrijf <b>bestaande</b> student in
                      </a>
                    );
                  } else {
                    return checkAllCasesHandled(f.parsed);
                  }
                } else {
                  return <span></span>;
                }
              })}
            </article>
          );
        },
      };
    })
    .addField(
      "student",
      ["new_or_existing"],
      async function ({ new_or_existing }): Promise<
        Field<
          | {
              tag: "new";
              firstname: string;
              lastname: string;
              email: string;
              phone: string;
              birthday: joda.LocalDate | null;
            }
          | {
              tag: "existing";
              student: {
                id: studentid;
                firstname: string;
                lastname: string;
                birthday: joda.LocalDate | null;
              };
            }
        >
      > {
        if (new_or_existing === "new") {
          const init =
            initialstudent && initialstudent.tag === "new"
              ? initialstudent
              : {
                  firstname: "",
                  lastname: "",
                  email: "",
                  phone: "",
                  birthday: null,
                };
          return new Form({})
            .addField("tag", [], () => constantField("new" as const))
            .addField("firstname", [], () =>
              textBox({
                initialVal: init.firstname,
                label: "Voornaam",
                validations: [mandatoryValidation],
              }),
            )
            .addField("lastname", [], () =>
              textBox({
                initialVal: init.lastname,
                label: "Achternaam",
                validations: [mandatoryValidation],
              }),
            )
            .addField("_email", [], () =>
              textBox({
                initialVal: init.email,
                type: "email",
                label: "Email",
                validations: [mandatoryValidation, emailValidation],
              }),
            )
            .addField("email", ["_email"], ({ _email }) =>
              constantField(_email.toLowerCase()),
            )
            .addField("phone", [], () =>
              textBox({
                initialVal: init.phone,
                label: "Telefoon",
              }),
            )
            .addField(
              "birthday",
              [],
              (): Promise<Field<joda.LocalDate | null>> =>
                country === "Belgium"
                  ? constantField(null)
                  : country === "Netherlands"
                  ? dateBox(
                      init.birthday !== null
                        ? {
                            initialVal: init.birthday,
                            label: "Geboortedatum",
                          }
                        : { label: "Geboortedatum" },
                    )
                  : checkAllCasesHandled(country),
            )
            .build({
              render: (fields) => (
                <div>
                  <div className={style.equal_columns_non_responsive}>
                    <div style="margin-right: 16px">{fields.firstname()}</div>
                    {fields.lastname()}
                  </div>
                  {country === "Belgium" ? (
                    <div className={style.equal_columns_non_responsive}>
                      <div style="margin-right: 16px">{fields._email()}</div>
                      {fields.phone()}
                    </div>
                  ) : country === "Netherlands" ? (
                    <div>
                      <div className={style.equal_columns_non_responsive}>
                        <div style="margin-right: 16px">
                          {fields.birthday()}
                        </div>
                        {fields.phone()}
                      </div>
                      {fields._email()}
                    </div>
                  ) : (
                    checkAllCasesHandled(country)
                  )}
                </div>
              ),
            });
        } else if (new_or_existing === "existing") {
          return new Form({})
            .addField("tag", [], () => constantField("existing" as const))
            .addField("student", [], async function () {
              return typeaheadField(
                ({ searchstring }) =>
                  rpc(routes.searchStudentSpec, undefined, { searchstring }),
                renderFirstNameLastName,
                {
                  maxCandidatesToShow: 5,
                  initial:
                    initialstudent && initialstudent.tag === "existing"
                      ? initialstudent.student
                      : null,
                  label: "Student",
                },
              );
            })
            .build();
        } else {
          return checkAllCasesHandled(new_or_existing);
        }
      },
    )
    .addField("trial_lesson", ["student"], () => {
      if (
        lg.tag === "private" ||
        (lg.tag === "group" && lg.allow_trial_lessons === false)
      ) {
        return constantField(false);
      } else {
        return checkBox({
          initialVal: false,
          class: style.checkbox + style.input_like,
          label: "Proefles",
        });
      }
    })
    .addField(
      "invoice_and_expected_payments",
      ["student", "trial_lesson"],
      async ({ student, trial_lesson }) => {
        const allInvoicesLines: (invoicelineStudent & {
          type_: invoicelinetype;
        })[] = adaptInvoiceLinesToStudent(
          country,
          schoolurl,
          student.tag === "new" ? student.birthday : student.student.birthday,
          {
            tag: "applicable",
            lessons: {
              tag: "lessongroup",
              lessons: dates.lessons,
              startedlessonson: dates.startedlessonson,
            },
          },
          lg.tag === "private"
            ? lg.ils
            : await rpc(routes.getInvoiceLinesForLessongroupSpec, undefined, {
                lessongroupid: lg.lessongroupid,
                trial_lesson,
              }),
        );
        return invoiceAndExpectedPaymentsField(
          allInvoicesLines,
          [],
          trial_lesson &&
            lg.tag === "group" &&
            lg.trial_lesson_payment_scheme_id !== null
            ? [
                await rpc(routes.getNonPeriodicPaymentSchemeSpec, undefined, {
                  id: lg.trial_lesson_payment_scheme_id,
                }),
              ].compact()
            : payment_schemes,
          student.tag === "new"
            ? { tag: "new", birthday: student.birthday }
            : {
                tag: "existing",
                id: student.student.id,
                birthday: student.student.birthday,
              },
          periodid,
          {
            periodfee_required: opts.periodfee_required,
            max_one_optional_line: opts.max_one_optional_line,
            each_period_fee_due_date,
            each_period_fee_pay_during_online,
            dates,
            payments: [], // New enrollment -> no payments done yet
          },
        );
      },
    )
    .build({
      render: (fields) => (
        <div>
          {fields.student()}
          {fields.new_or_existing()}
          {fields.trial_lesson()}
          {fields.invoice_and_expected_payments()}
        </div>
      ),
    });
}

export function invoiceField<
  IL extends {
    amount: currency;
    vat: number;
    description: string;
    type_: invoicelinetype;
  },
>(
  allInvoicesLines: IL[],
  requestedOptionalInvoiceLines: { description: string }[],
  formula: { max_one_optional_line: boolean },
  opts?: { label?: string },
): Field<Omit<IL, "type_">[]> {
  const normalInvoicelines = allInvoicesLines.filter(
    (il) => il.type_ === "Normal",
  );
  const optionalInvoiceLines = allInvoicesLines.filter(
    (il) => il.type_ !== "Normal",
  );

  const s = new Source({
    tag: "parsed" as const,
    parsed: allInvoicesLines.filter(
      (il) =>
        il.type_ === "Normal" ||
        requestedOptionalInvoiceLines.some(
          (reqil) => reqil.description === il.description,
        ),
    ),
  });
  s.observe((s) => console.log(JSON.stringify(s, null, 2)));

  return {
    s,
    cleanup: () => {},
    render: function () {
      const optionalInvoicelinesSection =
        optionalInvoiceLines.length === 0 ? (
          <span></span>
        ) : (
          <div style="margin: 8px 0px -8px 0px;">
            <table
              className={style.table_has_inputs}
              style="table-layout: fixed; margin-bottom: 4px"
            >
              {formula.max_one_optional_line === true ? (
                (() => {
                  const content = dyn(s, (sel) => {
                    return radioFieldCheckboxRenderInputLike(
                      "Geen",
                      sel.parsed.filter((il) => il.type_ !== "Normal")
                        .length === 0,
                      () =>
                        s.set({
                          tag: "parsed" as const,
                          parsed: normalInvoicelines,
                        }),
                      { style: "margin: 0px;" },
                    );
                  });
                  return (
                    <tr>
                      <td>{content}</td>
                    </tr>
                  );
                })()
              ) : (
                <span></span>
              )}
              {optionalInvoiceLines.map((il) => {
                return (
                  <tr>
                    <td>
                      {dyn(s, (sel) =>
                        radioFieldCheckboxRenderInputLike(
                          il.description + " " + renderCurrency(il.amount),
                          sel.parsed.some((selil) => isEqual(il, selil)),
                          () => {
                            if (formula.max_one_optional_line) {
                              // "Radio"
                              s.set({
                                tag: "parsed" as const,
                                parsed: normalInvoicelines.concat(il),
                              });
                            } else {
                              // "Checkbox"
                              s.set({
                                tag: "parsed" as const,
                                parsed: sel.parsed.some((selil) =>
                                  isEqual(selil, il),
                                )
                                  ? sel.parsed.filter(
                                      (selil) => !isEqual(selil, il),
                                    )
                                  : sel.parsed.concat(il),
                              });
                            }
                          },
                          { style: "margin: 0px;" },
                        ),
                      )}
                    </td>
                  </tr>
                );
              })}
            </table>
          </div>
        );

      return normalInvoicelines.length > 0 ||
        optionalInvoiceLines.length > 0 ? (
        <div className={style.formsection_informational}>
          <div>
            <b>{opts?.label ?? "Factuur"}</b>
            <span>
              {": " +
                renderCurrency(
                  normalInvoicelines.reduce(
                    (acc, il) => acc.add(il.amount),
                    currency(0),
                  ),
                )}
            </span>
          </div>
          {optionalInvoicelinesSection}
        </div>
      ) : (
        <span></span>
      );
    },
  };
}

export async function paymentSchemeField(
  payment_schemes: (non_periodic_payment_scheme | periodic_payment_scheme)[],
): Promise<Field<periodic_payment_scheme | non_periodic_payment_scheme>> {
  const values = payment_schemes.orderBy(
    [(p) => (p.tag === "non_periodic" ? 1 : 2), (p) => p.label],
    ["asc", "asc"],
  );

  if (values.length === 0) {
    throw new Error("Both payment schemes null");
  }
  if (values.length === 1) {
    return constantField(payment_schemes[0]);
  }

  return selectBox({
    initial: values[0],
    values: values,
    label: "Betalingsschema",
    show: renderPaymentScheme,
  });
}

export async function expectedPaymentField(
  payment_scheme: periodic_payment_scheme | non_periodic_payment_scheme,
  invoice: {
    amount: price_adapted_to_lg_and_student;
    from_each_period_fee?: never;
  }[],
  dates: {
    firstlesson: joda.LocalDate;
    lastlesson: joda.LocalDate;
    period_firstday: joda.LocalDate;
    created: joda.LocalDate;
  },
  payments: {
    amount: currency;
    paymentproviderid: string | null;
  }[],
): Promise<
  Field<
    {
      amount: currency;
      date: joda.LocalDate;
      pay_during_online_enrollment: boolean;
      periodic_payment_method: periodic_payment_method | null;
    }[]
  >
> {
  const eps = calculateExpectedPayments(payment_scheme, invoice, dates);
  type ep = {
    amount: currency;
    date: joda.LocalDate;
    pay_during_online_enrollment: boolean;
    periodic_payment_method: periodic_payment_method | null;
  };

  const totalPaid = payments.reduce((acc, p) => acc.add(p.amount), currency(0));

  // Calculate paid expected payments
  const [_am, epsWithPaid] = eps.reduce(
    (
      [amountPaidLeft, epsWithPaid]: [currency, (ep & { paid: boolean })[]],
      ep: ep,
    ) => {
      const res: [currency, (ep & { paid: boolean })[]] = [
        amountPaidLeft.subtract(ep.amount),
        [
          ...epsWithPaid,
          { ...ep, paid: amountPaidLeft.value >= ep.amount.value },
        ],
      ];
      return res;
    },
    [totalPaid, []],
  );

  const nonPaidEps = epsWithPaid.filter((ep) => ep.paid === false);

  const f = await makeArrayField<ep>({
    initialVal: nonPaidEps,
    render: oneLineArrayRender(() => false, { allowNewAndRemove: false }),
    makeSubForm: async function (t: {
      amount: currency;
      date: joda.LocalDate;
      pay_during_online_enrollment: boolean;
      periodic_payment_method: periodic_payment_method | null;
    }) {
      return new Form({})
        .addField("amount", [], () =>
          constantFieldWithRender(
            t.amount,
            <div className={style.input_like}>{renderCurrency(t.amount)}</div>,
          ),
        )
        .addField("date", [], () => dateBox({ initialVal: t.date }))
        .addField("pay_during_online_enrollment", [], () =>
          constantField(t.pay_during_online_enrollment),
        )
        .addField("periodic_payment_method", [], () =>
          constantField(t.periodic_payment_method),
        )
        .build();
    },
    makeNew: function () {
      // dummy
      return {
        amount: currency(0),
        date: joda.LocalDate.now(),
        pay_during_online_enrollment: false,
        periodic_payment_method: null,
      };
    },
  });
  return {
    ...f,
    render: () => {
      const editingS = new Source(false);
      return dyn(editingS, (editing) => {
        if (editing === false) {
          const editButton = (
            <button
              className={
                style.button +
                style.button_has_only_icon +
                style.button_round +
                style.button_white
              }
              style={`margin-left: 8px;
                      position: absolute;
                      top: 4px;
                      right: 4px;
                      border-radius: 8px 8px 8px 8px;
                      max-height: calc(100% - 8px); `}
              onclick={() => editingS.set(true)}
            >
              <i className={FA.icon_pencil_alt_solid}></i>
            </button>
          );
          const nonPaidEpsRendered =
            nonPaidEps.length === 0 ? (
              <span></span>
            ) : (
              <div className={style.formsection_informational}>
                {nonPaidEps.length === 1 ? (
                  <div>
                    <b>{"Verwachte betaling"}</b>
                    <span>
                      {": " +
                        ddMMMyyyy(nonPaidEps[0].date) +
                        ` - ${renderCurrency(nonPaidEps[0].amount)}`}
                    </span>
                    {editButton}
                  </div>
                ) : (
                  <div>
                    <b>
                      {"Verwachte betalingen"}
                      {editButton}
                    </b>
                    <ul>
                      {nonPaidEps
                        .orderBy((ep) => ep.date.toEpochDay(), "asc")
                        .map((ep) => (
                          <li>
                            {ddMMMyyyy(ep.date) +
                              ` - ${renderCurrency(ep.amount)}`}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            );

          const paymentRendered =
            totalPaid.value > 0 ? (
              <div className={style.formsection_informational}>
                <div>
                  <b>{"Online betaald"}</b>
                  <span>{`: ${renderCurrency(totalPaid)}`}</span>
                </div>
              </div>
            ) : (
              <span></span>
            );

          return [nonPaidEpsRendered, paymentRendered];
        } else {
          return (
            <div>
              <label for="">{"Verwachte betalingen"}</label>
              {f.render()}
            </div>
          );
        }
      });
    },
  };
}

export function invoiceAndExpectedPaymentsField(
  adaptedInvoiceLines: (invoicelineStudent & { type_: invoicelinetype })[],
  requestedOptionalInvoiceLines: invoicelineRequestedOptional[],
  payment_schemes: (periodic_payment_scheme | non_periodic_payment_scheme)[],
  student:
    | { tag: "existing"; birthday: joda.LocalDate | null; id: studentid }
    | { tag: "new"; birthday: joda.LocalDate | null },
  periodid: periodid,
  opts: {
    max_one_optional_line: boolean;
    periodfee_required: boolean;
    each_period_fee_due_date: singleDueDateCalculation;
    each_period_fee_pay_during_online: boolean;
    dates: {
      firstlesson: joda.LocalDate;
      lastlesson: joda.LocalDate;
      period_firstday: joda.LocalDate;
      created: joda.LocalDate;
    };
    payments: {
      amount: currency;
      paymentproviderid: string | null;
    }[];
  },
  // For testing
  rpcImpl?: <Parameters, Body, Returns>(
    spec: APISpec<Parameters, Body, Returns>,
    params: Parameters,
    b: Body,
  ) => Promise<Returns>,
): Field<{
  invoice: invoicelineStudent[];
  payment_scheme: non_periodic_payment_scheme | periodic_payment_scheme;
  expected_payments: {
    amount: currency;
    date: joda.LocalDate;
    pay_during_online_enrollment: boolean;
    periodic_payment_method: periodic_payment_method | null;
  }[];
  periodfee: periodfeeAfterChoice;
}> {
  const form = new Form({})
    .addField(
      "periodfee",
      [],
      async (): Promise<Field<periodfeeAfterChoice>> => {
        const pf = await (rpcImpl || rpc)(
          routes.getPeriodfeeForStudentSpec,
          undefined,
          {
            periodid,
            student: student,
            firstlesson: opts.dates.firstlesson,
            formula: { periodfee_required: opts.periodfee_required },
          },
        );

        if (pf === null) {
          return constantField(null);
        } else if (pf.tag === "new") {
          const pfnew = pf;
          return new Form({})
            .addField("tag", [], () => constantField("new" as const))
            .addField("lines", [], async () =>
              invoiceField(
                pfnew.lines,
                requestedOptionalInvoiceLines,
                { max_one_optional_line: opts.max_one_optional_line },
                {
                  label: "Jaargeld",
                },
              ),
            )
            .build();
        } else if (pf.tag === "existing") {
          return constantFieldWithRender(
            pf,
            <div className={style.formsection_informational}>
              <div>
                <b>{"Jaargeld reeds aangemaakt"}</b>
              </div>
              <div>
                {renderCurrency(
                  (pf.invoice.lines || []).reduce(
                    (acc, il) => acc.add(il.amount),
                    currency(0),
                  ),
                )}
                {pf.invoice.amount_paid.value > 0 ? (
                  <span>
                    {" (" +
                      "%n betaald".replace(
                        "%n",
                        renderCurrency(pf.invoice.amount_paid),
                      ) +
                      ")"}
                  </span>
                ) : (
                  <span></span>
                )}
              </div>
            </div>,
          );
        } else {
          return checkAllCasesHandled(pf);
        }
      },
    )
    .addField(
      "invoice_without_extra_cost",
      [],
      async (): Promise<Field<invoicelineStudent[]>> => {
        if (adaptedInvoiceLines.length === 0) {
          return constantFieldWithRender(
            [],
            <div className={style.formsection_informational}>
              {"Er zal geen factuur worden aangemaakt"}
            </div>,
          );
        }
        return invoiceField(
          adaptedInvoiceLines,
          requestedOptionalInvoiceLines,
          { max_one_optional_line: opts.max_one_optional_line },
        );
      },
    )
    .addField("payment_scheme", [], async () =>
      paymentSchemeField(payment_schemes),
    )
    .addField(
      "invoice",
      ["invoice_without_extra_cost", "payment_scheme"],
      async ({
        invoice_without_extra_cost,
        payment_scheme,
      }): Promise<Field<invoicelineStudent[]>> => {
        const extraCost = getExtraCostInvoiceLine(payment_scheme);
        return constantField([
          ...invoice_without_extra_cost,
          ...[extraCost].compact().map((il) => ({
            ...il,
            // TODO, don't want to handle this now...
            amount:
              il.amount_with_vat as unknown as price_adapted_to_lg_and_student,
          })),
        ]);
      },
    )
    .addField(
      "expected_payments",
      ["invoice", "payment_scheme"],
      async ({ invoice, payment_scheme }) => {
        return expectedPaymentField(
          payment_scheme,
          invoice,
          opts.dates,
          opts.payments,
        );
      },
    )
    .build();
  return form;
}
